import {
  Component,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { debounceTime, lastValueFrom, Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { scrollContentWidth } from 'src/shared/components/scroll-wrapper/scroll-wrapper.component';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { OnboardingDummyDataService } from 'src/shared/services/onboarding-dummy-data.service';
import { ModalController } from '@ionic/angular';
import { AddUpsellPage } from './add-upsell/add-upsell';
import { UpsellSettingsPage } from './upsell-settings/upsell-settings';
import { IntegratePage } from './integrate/integrate';
import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { ConciergeQueriesService } from 'src/shared/services/concierge-queries.service';
import { Loading } from 'src/shared/components/loading/loading';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { Addresses } from 'src/providers/customer/addresses';
import { Client } from 'src/providers/client/client';
import { AgentsProvider } from 'src/providers/agents/agents';
import { Events } from 'src/providers/events/events';

@Component({
    templateUrl: './upsells.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./upsells.scss'],
    standalone: false
})
export class UpsellsPage implements OnInit, OnDestroy {
  currentRows: any;
  readonly desktopWidthContent: string = scrollContentWidth.LARGE;
  form: UntypedFormGroup;
  filteredRows: any = [];
  panelClosedSubscription$: Subscription;
  headers: any = ['Property', 'Reservation', 'Request', 'last-column'];
  rows: any;
  showLoadingSpinner: boolean;
  loaded: boolean;
  addressResponse: any;
  addressId: any;
  addressItems: any;
  propertyForm: UntypedFormGroup;
  shownRowsCount = 50;
  showAboutThisPage: boolean;
  errorMessage: string;
  isSearching = false;
  searchSubscription$: Subscription;
  routeReused: boolean;
  @ViewChild('tidyTable') tidyTable: any;

  upsells: any[];
  upsellTableItems: any[];
  upsellsLoaded: boolean;
  totalUpsells: string;

  private propertiesCache: { [key: number]: any } = {};
  private reservationsCache: { [key: number]: any } = {};
  private readonly fb = inject(UntypedFormBuilder);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  readonly windowService = inject(WindowService);
  private readonly util = inject(Util);
  private readonly aboutThisPageService = inject(AboutThisPageService);
  private readonly onboardingProvider = inject(OnboardingProvider);
  private readonly onboardingDummyDataService = inject(OnboardingDummyDataService);
  private readonly modalController = inject(ModalController);
  private readonly conciergeApi = inject(ConciergeApiService);
  private readonly conciergeQueries = inject(ConciergeQueriesService);
  private readonly guestReservations = inject(GuestReservations);
  private readonly addresses = inject(Addresses);
  private readonly client = inject(Client);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly agentsProvider = inject(AgentsProvider);
  private readonly events = inject(Events);

  constructor() {
    this.form = this.fb.group({
      search: [''],
    });
    this.propertyForm = this.fb.group({
      property: [''],
    });
    this.searchSubscription$ = this.form
      .get('search')
      .valueChanges.pipe(debounceTime(300))
      .subscribe((value) => {
        try {
          if (!this.showAboutThisPage) {
            this.updateSearch({ search: value });
          }
        } catch (_error) {
          // Fallback to showing all rows if search fails
          this.currentRows = [...this.rows.slice(0, this.shownRowsCount)];
        }
      });
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('upsells');
  }

  async ngOnInit() {
    try {
      this.addressResponse = await this.client.getAddresses();
      this.loadPage();

      // Add keyboard shortcuts for search
      this.setupKeyboardShortcuts();
    } catch (err) {
      this.util.showError(
        err.error && err.error.message ? err.error.message : err.message,
        10000
      );
    }
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.upsells = [];
    this.rows = [];
    this.currentRows = [];
    this.filteredRows = [];
    this.cdr.markForCheck();
    this.loadPage(true);
  }

  setupKeyboardShortcuts() {
    // Listen for keyboard events on the search input
    const searchInput = document.querySelector('input[formcontrolname="search"]');
    if (searchInput) {
      searchInput.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Escape' && !this.showAboutThisPage) {
          this.clearSearch();
          (event.target as HTMLInputElement).blur();
        }
      });
    }
  }

  async loadPage(showLoadingSpinner = false, setStoredRoute = true) {
    if (showLoadingSpinner) {
      this.showLoadingSpinner = true;
      this.cleanup();
    }
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('upsells');
    this.rows = [];
    this.upsellsLoaded = false;
    this.errorMessage = '';

    try {
      await this.getUpsells();
      this.rows = this.upsellTableItems || [];
      this.buildHeaders();
      this.currentRows = [...this.rows.slice(0, this.shownRowsCount)];
    } catch (err) {
      this.handleError(err);
    }

    this.showLoadingSpinner = false;
    this.loaded = true;
    if (this.windowService.isDesktopRes && setStoredRoute) {
      await this.storage.setStoredRoute('/upsells');
      this.routeReused = true;
    }
  }

  ngOnDestroy() {
    this.cleanup();
  }

  cleanup() {
    if (this.panelClosedSubscription$) {
      this.panelClosedSubscription$.unsubscribe();
    }
    if (this.searchSubscription$) {
      this.searchSubscription$.unsubscribe();
    }
  }

  @Loading('', true)
  async getUpsells() {
    try {
      if (this.showAboutThisPage) {
        const upsellsMockData = this.onboardingDummyDataService.getUpsellsMockData();
        this.upsells = upsellsMockData;
        this.totalUpsells = upsellsMockData.length.toString();
      } else {
        // Use TanStack Query for caching and deduplication
        // Reset to page 1 and refetch
        this.conciergeQueries.setUpsellsPage(1);
        await this.conciergeQueries.upsellsQuery.refetch();
        this.upsells = this.conciergeQueries.upsells();
        this.totalUpsells = this.conciergeQueries.totalUpsells();
      }
      this.upsellTableItems = await this.buildRows(this.upsells);
      this.upsellsLoaded = true;
    } catch (err) {
      this.handleError(err);
    }
  }

  async fetchItems(filterParams: { pageIndex: number, pageSize: number }, params?: any): Promise<any> {
    const isPending = false;
    const itemsResponse = await lastValueFrom(this.conciergeApi.getConciergeItems(`&concierge_item_type_key=early_checkin,late_checkout,midstay_cleaning&page=${filterParams.pageIndex}&per_page=${filterParams.pageSize}&is_marked_done=${isPending}${params ? `${params}` : ''}`));
    this.totalUpsells = itemsResponse.totalRecords;
    return itemsResponse.body;
  }

  async buildRows(items: any[] = []) {
    if (items.length > 50) {
      items = items.slice(0, 50);
    }

    const rows = [];
    for (const item of items) {
      // Extract data from concierge item structure
      const component = item.components?.[0]?.value;
      const _subject = component?.subject || item.header_text || 'N/A';
      const _status = component?.status || 'N/A';
      const _lastUpdate = component?.last_update_at ? this.parseDate(component.last_update_at) : 'N/A';

      // Parse metadata.request_data.body to extract property, reservation, and requestType
      let propertyValue = 'N/A';
      let reservationValue = 'N/A';
      let requestTypeValue = 'N/A';
      let addressId: any = 'N/A';
      let reservationId: any = 'N/A';

      if (item.metadata?.request_data?.body) {
        try {
          // Parse the metadata.request_data.body string to extract the values
          addressId = Number(item.metadata.request_data.body.match(/property:\s*(\d+)/)?.[1]);
          reservationId = Number(item.metadata.request_data.body.match(/reservation:\s*(\d+)/)?.[1]);
          const bodyData = await this.parseMetadataBody(item.metadata.request_data.body);
          propertyValue = bodyData.property ? bodyData.property : 'N/A';
          reservationValue = bodyData.reservation ? bodyData.reservation : 'N/A';
          requestTypeValue = bodyData.requestType ? this.humanizeRequestType(bodyData.requestType) : 'N/A';

          if (this.showAboutThisPage) {
            // Mock data parsing debug removed
          }
        } catch (_err) {
      // Intentionally empty - errors handled elsewhere
        }
      }

      rows.push({
        id: item.id,
        addressId,
        'Property': {
          value: propertyValue,
          action: null,
          color: this.addressResponse ? this.addressResponse.find((address) => address.id === addressId)?.color || '#1E2125' : '#1E2125',
          addressId
        },
        'Reservation': {
          value: reservationValue,
          reservationId,
          action: null
        },
        'Request': {
          value: requestTypeValue,
          action: null
        },
        'last-column': {
          value: item.id,
          action: null,
          hasChevron: true
        }
      });
    }

    return rows;
  }

    parseDate(dateStr) {
    if (this.showAboutThisPage) {
      // For onboarding, return a formatted mock date
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
          return 'N/A';
        }
        return `${new ParseDateTimePipe().transform(dateStr, 'MMM d', 'local')  } ${  new ParseDateTimePipe().transform(dateStr, 'h:mma', 'local')}`;
      } catch (_err) {
        return 'N/A';
      }
    }

    return `${new ParseDateTimePipe().transform(dateStr, 'MMM d', 'local')  } ${  new ParseDateTimePipe().transform(dateStr, 'h:mma', 'local')}`;
  }

    async parseMetadataBody(bodyString: string): Promise<{ property?: string; reservation?: string; requestType?: string }> {
    try {
      // The body string contains JSON-like data, we need to extract the key-value pairs
      const result: { property?: string; reservation?: string; requestType?: string } = {};

      // Extract property
      const propertyMatch = bodyString.match(/property:\s*(\d+)/);
      if (propertyMatch) {
        const propertyId = parseInt(propertyMatch[1]);
        result.property = await this.getPropertyName(propertyId);
      }

      // Extract reservation
      const reservationMatch = bodyString.match(/reservation:\s*(\d+)/);
      if (reservationMatch) {
        const reservationId = parseInt(reservationMatch[1]);
        result.reservation = await this.getReservationDates(reservationId);
      }

      // Extract requestType
      const requestTypeMatch = bodyString.match(/requestType:\s*(\w+)/);
      if (requestTypeMatch) {
        result.requestType = requestTypeMatch[1];
      }

      return result;
    } catch (_err) {
      return {};
    }
  }

    humanizeRequestType(requestType: string): string {
    // Convert requestType to human-readable format
    const requestTypeMap: { [key: string]: string } = {
      'early_checkin': new TranslationPipe().transform('Early Check-in'),
      'late_checkout': new TranslationPipe().transform('Late Check-out'),
      'cleaning': new TranslationPipe().transform('Cleaning'),
      'maintenance': new TranslationPipe().transform('Maintenance'),
      'concierge': new TranslationPipe().transform('Concierge Service'),
      'upsell': new TranslationPipe().transform('Upsell')
    };

    return requestTypeMap[requestType] || requestType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  async getPropertyName(propertyId: number): Promise<string> {
    // Check if property is already cached
    if (this.propertiesCache[propertyId]) {
      return this.propertiesCache[propertyId];
    }

    if (this.showAboutThisPage) {
      // Use mock property names for onboarding
      const mockPropertyNames = [
        'Beach House', 'Mountain Cabin', 'City Apartment', 'Lake House', 'Downtown Loft',
        'Ocean View Villa', 'Forest Retreat', 'Urban Penthouse', 'Riverside Cottage', 'Hillside Manor',
        'Coastal Bungalow', 'Alpine Chalet', 'Metropolitan Suite', 'Garden Estate', 'Valley View Lodge'
      ];
      const mockName = mockPropertyNames[propertyId % mockPropertyNames.length] || `Property #${propertyId}`;
      this.propertiesCache[propertyId] = mockName;
      return mockName;
    }

    try {
      // Fetch property data and cache it
      const property = await this.addresses.getAddress(propertyId);
      const propertyName = property.address_name || `Property #${propertyId}`;
      this.propertiesCache[propertyId] = propertyName;
      return propertyName;
    } catch (_err) {
      const fallbackName = `Property #${propertyId}`;
      this.propertiesCache[propertyId] = fallbackName;
      return fallbackName;
    }
  }

  async getReservationDates(reservationId: number): Promise<string> {
    // Check if reservation is already cached
    if (this.reservationsCache[reservationId]) {
      return this.reservationsCache[reservationId];
    }

    if (this.showAboutThisPage) {
      // Use mock reservation dates for onboarding with current year
      const currentYear = new Date().getFullYear();
      const mockDates = [
        `Jan 15-20, ${currentYear}`,
        `Feb 5-10, ${currentYear}`,
        `Mar 8-13, ${currentYear}`,
        `Apr 1-6, ${currentYear}`,
        `May 10-15, ${currentYear}`,
        `Jun 5-12, ${currentYear}`,
        `Jul 10-15, ${currentYear}`,
        `Aug 1-7, ${currentYear}`,
        `Sep 20-25, ${currentYear}`,
        `Oct 3-8, ${currentYear}`,
        `Nov 12-17, ${currentYear}`,
        `Dec 20-27, ${currentYear}`
      ];
      const mockDate = mockDates[reservationId % mockDates.length] || `Reservation #${reservationId}`;
      this.reservationsCache[reservationId] = mockDate;
      return mockDate;
    }

    try {
      // Fetch reservation data and cache it
      const reservation = await this.guestReservations.getReservationById(reservationId);
      const reservationDates = this.getReservationViewValue(reservation);
      this.reservationsCache[reservationId] = reservationDates;
      return reservationDates;
    } catch (_err) {
      const fallbackDates = `Reservation #${reservationId}`;
      this.reservationsCache[reservationId] = fallbackDates;
      return fallbackDates;
    }
  }

    getReservationViewValue(reservation: any) {
    const checkInDate = reservation?.source_data?.guest_reservation?.check_in_date || reservation?.source_data?.check_in_date;
    const checkOutDate = reservation?.source_data?.guest_reservation?.check_out_date || reservation?.source_data?.check_out_date;
    return `${new ParseDateTimePipe().transform(
      checkInDate,
      'MMM d'
    )} ${new ParseDateTimePipe().transform(
      checkInDate,
      'h:mma'
    )} - ${new ParseDateTimePipe().transform(
      checkOutDate,
      'MMM d'
    )} ${new ParseDateTimePipe().transform(
      checkOutDate,
      'h:mma '
    )}`;
  }

  handleError(err) {
    if (this.showAboutThisPage) {
      return;
    }

    this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
  }



  buildHeaders() {
    this.headers = ['Property', 'Reservation', 'Request', 'last-column'];
  }

  clearSearch() {
    if (this.showAboutThisPage) {
      return;
    }

    this.form.get('search')?.setValue('');
    this.filteredRows = [];
    this.currentRows = [];
    this.currentRows = [...this.rows.slice(0, this.shownRowsCount)];
    this.shownRowsCount = 50;
    this.isSearching = false;

    // Force refresh the table to ensure it updates
    this.forceTableRefresh();

    // Force change detection multiple times to ensure it works
    this.cdr.detectChanges();
    setTimeout(() => this.cdr.detectChanges(), 50);
    setTimeout(() => this.cdr.detectChanges(), 100);
  }

  // Getter for table data source to ensure proper change detection
  get tableDataSource() {
    return this.currentRows || [];
  }

  // Force refresh the table component
  forceTableRefresh() {
    if (this.showAboutThisPage) {
      return;
    }

    if (this.tidyTable) {
      // Try to force the table to refresh its data
      try {
        // Access internal methods if available
        if (this.tidyTable.refresh) {
          this.tidyTable.refresh();
        }
        if (this.tidyTable.ngOnChanges) {
          this.tidyTable.ngOnChanges({});
        }
      } catch (_error) {
      // Intentionally empty - errors handled elsewhere
      }
    }
  }

  updateSearch({ search }) {
    if (this.showAboutThisPage) {
      return;
    }

    this.isSearching = true;
    this.filteredRows = [];

    // Use setTimeout to allow UI to update before heavy search operation
    setTimeout(() => {
      try {
        if (search && search.trim() !== '') {
          const searchTerm = search.toLowerCase().trim();

          this.rows.forEach((row) => {
            let match = false;

            // Search through all relevant fields with intelligent matching
            this.headers.forEach((header) => {
              if (header !== 'last-column' && row[header]) {
                const field = row[header];

                // Check if the field has a value property
                if (field.value && typeof field.value === 'string') {
                  const fieldValue = field.value.toLowerCase();

                  // Exact match gets higher priority
                  if (fieldValue === searchTerm) {
                    match = true;
                  }
                  // Partial match
                  else if (fieldValue.includes(searchTerm)) {
                    match = true;
                  }
                  // Word boundary match (starts with search term)
                  else if (fieldValue.startsWith(searchTerm)) {
                    match = true;
                  }
                }

                // Also check additional properties that might contain searchable data
                if (field.addressId && field.addressId.toString().includes(searchTerm)) {
                  match = true;
                }

                if (field.reservationId && field.reservationId.toString().includes(searchTerm)) {
                  match = true;
                }
              }
            });

            // Additional search through row metadata if available
            if (row.addressId && row.addressId.toString().includes(searchTerm)) {
              match = true;
            }

            if (match) {
              this.filteredRows.push(row);
            }
          });

          // Sort results by relevance (exact matches first, then partial matches)
          this.filteredRows.sort((a, b) => {
            const aScore = this.getSearchRelevanceScore(a, searchTerm);
            const bScore = this.getSearchRelevanceScore(b, searchTerm);
            return bScore - aScore; // Higher score first
          });

          // Update current rows with filtered results - create new array reference
          this.currentRows = [];
          this.currentRows = [...this.filteredRows.slice(0, this.shownRowsCount)];

          // Reset pagination
          this.shownRowsCount = 50;
        } else {
          // No search term, show all rows
          this.filteredRows = [];
          this.currentRows = [];
          this.currentRows = [...this.rows.slice(0, this.shownRowsCount)];
          this.shownRowsCount = 50;
        }

        // Force refresh the table to ensure it updates
        this.forceTableRefresh();

        // Force change detection multiple times to ensure it works
        this.cdr.detectChanges();
        setTimeout(() => this.cdr.detectChanges(), 50);
        setTimeout(() => this.cdr.detectChanges(), 100);
      } catch (_error) {
        // Fallback to showing all rows if search fails
        this.currentRows = [];
        this.currentRows = [...this.rows.slice(0, this.shownRowsCount)];
      } finally {
        this.isSearching = false;
      }
    }, 100); // Small delay to allow UI to update
  }

  getSearchRelevanceScore(row: any, searchTerm: string): number {
    if (this.showAboutThisPage) {
      return 0;
    }

    let score = 0;

    this.headers.forEach((header) => {
      if (header !== 'last-column' && row[header]) {
        const field = row[header];

        if (field.value && typeof field.value === 'string') {
          const value = field.value.toLowerCase();

          // Exact match gets highest score
          if (value === searchTerm) {
            score += 100;
          }
          // Starts with search term
          else if (value.startsWith(searchTerm)) {
            score += 50;
          }
          // Contains search term
          else if (value.includes(searchTerm)) {
            score += 25;
          }
        }
      }
    });

    return score;
  }

  sortChanged(sort) {
    if (this.showAboutThisPage) {
      return;
    }

    const multiplier = sort.direction === 'asc' ? 1 : -1;
    this.currentRows = this.currentRows.sort((a, b) => {
      if (a[sort.active] && b[sort.active]) {
        if (a[sort.active].value < b[sort.active].value) {return -1 * multiplier;}
        if (a[sort.active].value > b[sort.active].value) {return 1 * multiplier;}
      }
      return 0;
    });
  }

  goToAddUpsellPage() {
    if (this.showAboutThisPage) {
      return;
    }

    this.storage.save('editingUpsellData', null);
    this.storage.save('editingUpsellId', null);

    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(AddUpsellPage);
      this.panelClosedSubscription$ = this.rightSidePanelService
        .panelClosed()
        .subscribe(async () => {
          await this.refreshData();
        });
    } else {
      this.navCtrl.navigateForward('add-upsell');
    }
  }

  goToEditUpsellPage(upsellData: any, upsellId: string) {
    if (this.showAboutThisPage) {
      return;
    }

    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.storage.save('editingUpsellData', upsellData);
      this.storage.save('editingUpsellId', upsellId);
      this.rightSidePanelService.navigateTo('add-upsell', {
        editingUpsellData: upsellData,
        editingUpsellId: upsellId
      }, AddUpsellPage, false);
      this.panelClosedSubscription$ = this.rightSidePanelService
        .afterPanelClosed()
        .subscribe(async () => {
          await this.refreshData();
        });
    } else {
      this.navCtrl.navigateForward('add-upsell', {
        editingUpsellData: upsellData,
        editingUpsellId: upsellId
      });
    }
  }

  onTableColumnClick(event: { column: string; element: any }) {
    if (this.showAboutThisPage) {
      return;
    }

    // Navigate to upsell details or edit page
    if (event.element['last-column'] && event.element['last-column'].value) {
      const upsellId = event.element['last-column'].value;

      // Extract upsell data from the table row
      const upsellData = {
        property: event.element['Property']?.addressId || event.element['Property']?.value,
        reservation: event.element['Reservation']?.reservationId,
        requestType: this.getRequestTypeFromDisplay(event.element['Request']?.value)
      };

      // Open add-upsell page for editing using the new method
      this.goToEditUpsellPage(upsellData, upsellId);
    }
  }

  getRequestTypeFromDisplay(displayValue: string): string {
    if (this.showAboutThisPage) {
      return 'early_checkin';
    }

    // Convert display value back to request type value
    const requestTypeMap: { [key: string]: string } = {
      'Early Check-in': 'early_checkin',
      'Early Check-In': 'early_checkin',
      'Late Check-out': 'late_checkout',
      'Late Check-Out': 'late_checkout',
      'Midstay Cleaning': 'midstay_cleaning'
    };

    return requestTypeMap[displayValue] || 'early_checkin';
  }

  async pageChange() {
    if (this.showAboutThisPage) {
      return;
    }

    const currentLength = this.currentRows.length;
    const totalLength =
      this.filteredRows.length > 0
        ? this.filteredRows.length
        : this.rows.length;
    const sourceArray =
      this.filteredRows.length > 0 ? this.filteredRows : this.rows;

    if (currentLength < totalLength) {
      const newRows = sourceArray.slice(
        currentLength,
        currentLength + this.shownRowsCount
      );
      this.currentRows = [...this.currentRows, ...newRows];
    } else if (this.upsells && this.currentRows.length < parseInt(this.totalUpsells)) {
      // Load more items from API if we have more data
      const nextPage = Math.floor(this.currentRows.length / 50) + 1;
      try {
        const newItems = await this.fetchItems({ pageIndex: nextPage, pageSize: 50 });
        const newTableItems = await this.buildRows(newItems);
        this.rows = [...this.rows, ...newTableItems];
        this.currentRows = [...this.currentRows, ...newTableItems];
      } catch (err) {
        this.handleError(err);
      }
    }
  }

  exportCSV() {
    if (this.showAboutThisPage) {
      return;
    }

    // Export upsells data to CSV
    if (this.upsells && this.upsells.length > 0) {
      // TODO: Implement CSV export functionality
      this.util.showSuccess(`Exporting ${this.upsells.length} upsells to CSV`, 3000);
    } else {
      this.util.showError('No upsells data to export', 3000);
    }
  }

  async openUpsellSettings() {
    if (this.showAboutThisPage) {
      return;
    }

    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(UpsellSettingsPage);
      this.panelClosedSubscription$ = this.rightSidePanelService
        .afterPanelClosed()
        .subscribe(async () => {
          await this.refreshData();
        });
    } else {
      this.navCtrl.navigateForward('upsell-settings');
    }
  }

  async openIntegrationInfo() {
    if (this.showAboutThisPage) {
      return;
    }

    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(IntegratePage);
      this.panelClosedSubscription$ = this.rightSidePanelService
        .afterPanelClosed()
        .subscribe(async () => {
          await this.refreshData();
        });
    } else {
      this.navCtrl.navigateForward('integrate');
    }
  }

  async refreshData() {
    if (this.showAboutThisPage) {
      return;
    }

    // Invalidate cached query data before refreshing
    this.conciergeQueries.invalidateUpsells();
    this.loaded = false;
    this.upsellsLoaded = false;
    this.rows = [];
    this.currentRows = [];
    this.filteredRows = [];
    await this.loadPage(true, false);
  }

}
